import { useState, useRef, useCallback, useEffect } from 'react';
import isEqual from 'lodash.isequal';

// types
import { ColumnType } from 'react-final-table';

// components
import IconTableSettings from '../icons/table-settings';
import IconClose from '../icons/close';
import Portal from '../portal/portal';
import CheckboxInput from '../checkbox/checkbox';

// hooks
import useAnimation from '../animations/use-animations';

// styles
import './table-settings.css';
import '../animations/animations.css';

export interface TableSettingsProps {
  headers: ColumnType<Record<string, unknown>>[];
  storeKey: string;
  onChange: (data: ColumnType<Record<string, unknown>>[]) => void;
}

export default function TableSettings({
  headers,
  storeKey,
  onChange,
}: TableSettingsProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  const modalRef = useRef<HTMLDivElement | null>(null);

  const isActive = useAnimation({
    componentRef: modalRef,
    onClose: () => setIsOpen(false),
    isOpen,
  });

  const onItemClick = useCallback(
    (header: ColumnType<Record<string, unknown>>) => {
      const nextHeaders = [...headers];
      const index = nextHeaders.indexOf(header);

      nextHeaders.splice(index, 1, {
        ...header,
        hidden: !header.hidden,
      });

      if (onChange) {
        onChange(nextHeaders);
      }

      if (storeKey) {
        localStorage.setItem(
          storeKey,
          JSON.stringify(
            nextHeaders.filter((h) => h.hidden).map((h) => h.name),
          ),
        );
      }
    },
    [headers, onChange, storeKey],
  );

  useEffect(() => {
    if (storeKey) {
      const selectedHeadersString = localStorage.getItem(storeKey);

      if (selectedHeadersString) {
        const selectedHeaders: string[] = JSON.parse(selectedHeadersString);
        const nextHeaders = headers.map((header) => ({
          ...header,
          hidden: selectedHeaders.includes(header.name),
        }));

        if (onChange && !isEqual(headers, nextHeaders)) {
          onChange(nextHeaders);
        }
      }
    }
  }, [storeKey, headers, onChange]);

  return (
    <>
      <div className="table-settings">
        <button
          type="button"
          className="table-settings-button"
          onClick={() => setIsOpen(!isOpen)}
          tabIndex={-1}
        >
          <span className="table-settings-button-text">Columns</span>
          <IconTableSettings />
        </button>
      </div>
      {(isActive || isOpen) && (
        <Portal>
          <div
            className={`animated-panel fullscreen table-settings-modal ${
              isActive && isOpen ? 'active' : ''
            }`}
            ref={modalRef}
          >
            <div className="animated-panel-content table-settings-modal-content">
              <div className="table-settings-modal-header">
                Table settings
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="table-settings-modal-close"
                  aria-label="close"
                >
                  <IconClose />
                </button>
              </div>
              <div className="table-settings-modal-children">
                <div className="table-settings-modal-children-title">
                  Show columns
                </div>
                {headers
                  .filter((header) => header.label)
                  .map((header) => (
                    <div key={header.label}>
                      {header.headerRender && (
                        <div className="table-settings-sub-title">
                          {header.headerRender({ label: '' })}
                        </div>
                      )}
                      <CheckboxInput
                        key={header.label}
                        name={header?.label as string}
                        checked={!header.hidden}
                        onChange={() => onItemClick(header)}
                        type="checkbox"
                        title={header?.label as string}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}
